import { useMemo } from 'react'
import { useTheme, alpha } from '@mui/material/styles'
import EChart from './EChart'
import { fmtHrs } from './vocabulary'

const OVER = '#e15759'
const UNDER = '#4e79a7'

const tooltipStyle = {
  backgroundColor: 'rgba(33, 33, 41, 0.94)',
  borderWidth: 0,
  textStyle: { color: '#fff', fontSize: 12 },
}

// Each day's transcribed hours as a share of that day's pinned allowance. A
// bar past 100% is what enforcing would have skipped; a day the allowance was
// never pinned (nothing reserved) has no bar rather than a zero.
const AllowanceChart = ({ daily, height = 220 }) => {
  const theme = useTheme()
  const rows = useMemo(
    () => daily.filter(({ books }) => books !== 'none'),
    [daily]
  )
  const option = useMemo(() => {
    const text = theme.palette.text.secondary
    const grid = alpha(text, 0.15)
    return {
      grid: { left: 44, right: 16, top: 16, bottom: 24 },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        ...tooltipStyle,
        formatter: ([point]) => {
          const row = rows[point.dataIndex]
          const skip = row.would_skip_hours
          return `<div style="min-width:200px">
            <div style="margin-bottom:4px">${row.day} UTC${
            row.books === 'running' ? ' · so far' : ''
          }</div>
            <div>${fmtHrs(row.transcribed_hours)} of ${fmtHrs(
            row.allowance_hours
          )} allowed</div>
            <div style="opacity:.75;margin-top:4px">Enforcing would have skipped:
              allowance ${fmtHrs(skip.allowance)} · credit bucket ${fmtHrs(
            skip.credits
          )} · unreviewed ${fmtHrs(skip.unreviewed)}</div>
          </div>`
        },
      },
      xAxis: {
        type: 'category',
        data: rows.map(({ day }) => day),
        axisLabel: { color: text, fontSize: 10, formatter: (d) => d.slice(5) },
        axisLine: { lineStyle: { color: grid } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        min: 0,
        max: (extent) => Math.max(100, Math.ceil(extent.max / 10) * 10),
        axisLabel: { color: text, fontSize: 10, formatter: (v) => `${v}%` },
        splitLine: { lineStyle: { color: grid } },
      },
      series: [
        {
          name: 'Share of the allowance',
          type: 'bar',
          barMaxWidth: 34,
          data: rows.map(({ transcribed_hours, allowance_hours, books }) => {
            if (!allowance_hours) return null
            const value = Math.round(
              (transcribed_hours / allowance_hours) * 100
            )
            return {
              value,
              itemStyle: {
                color: value > 100 ? OVER : UNDER,
                opacity: books === 'running' ? 0.45 : 1,
              },
            }
          }),
          markLine: {
            symbol: 'none',
            silent: true,
            lineStyle: { color: text, type: 'dashed' },
            label: {
              formatter: "the day's allowance",
              position: 'insideEndTop',
              color: text,
              fontSize: 10,
            },
            data: [{ yAxis: 100 }],
          },
        },
      ],
    }
  }, [rows, theme])

  return (
    <EChart
      option={option}
      height={height}
      isEmpty={!rows.length}
      emptyText="Nothing transcribed in this window."
    />
  )
}

export default AllowanceChart
